import {decorate, observable} from "mobx";
import Api from "../util/api";
import {messageErreur} from "./smsStore";
import {STATUTS_FINAUX} from "../util/terminal";

/** Intervalle de secours si le temps réel (laravel-echo) ne transmet rien. */
export const INTERVALLE_PAIEMENT_MS = 2000;
export const INTERVALLE_ETAT_MS = 30000;

/**
 * Lecteur de carte Zettle du comptoir : état du téléphone (bandeau d'alerte, bouton « Carte ») et suivi du
 * paiement en cours. Mises à jour par le canal privé « terminal », avec une interrogation régulière en secours.
 */
export default class TerminalStore {

    etat = null;      // état du téléphone renvoyé par GET appareil/etat
    paiement = null;  // paiement en cours de suivi (fenêtre d'attente), ou null

    constructor(rootStore) {
        this.rootStore = rootStore;
        this.api = new Api();
        this.minuteurEtat = null;
        this.minuteurPaiement = null;
        this.ecoute = false;
    }

    get pret() {
        return !!this.etat?.pret;
    }

    async chargerEtat() {
        try {
            this.etat = await this.api.terminal.etatAppareil();
        } catch (e) {
            // silencieux : le bandeau garde le dernier état connu
        }
    }

    /** Démarre le suivi de l'état du téléphone (bandeau présent sur toutes les pages d'administration). */
    surveiller() {
        this.ecouter();
        this.chargerEtat();
        if (!this.minuteurEtat) {
            this.minuteurEtat = setInterval(() => this.chargerEtat(), INTERVALLE_ETAT_MS);
        }
    }

    arreterSurveillance() {
        clearInterval(this.minuteurEtat);
        this.minuteurEtat = null;
    }

    ecouter() {
        if (this.ecoute || !this.rootStore.getEcho) {
            return;
        }
        try {
            this.rootStore.getEcho()
                .private("terminal")
                .listen("AppareilComptoirEvent", ev => {
                    if (ev.etat) {
                        this.etat = ev.etat;
                    }
                })
                .listen("PaiementTerminalEvent", ev => this.recevoir(ev.paiement));
            this.ecoute = true;
        } catch (e) {
            // pas de temps réel : l'interrogation régulière suffit
        }
    }

    /** Mise à jour du paiement suivi (temps réel ou interrogation) ; ignore les autres paiements. */
    recevoir(paiement) {
        if (!paiement || !this.paiement || paiement.id !== this.paiement.id) {
            return;
        }
        this.paiement = paiement;
        if (STATUTS_FINAUX.includes(paiement.statut)) {
            this.arreterSuivi();
        }
    }

    /** Envoie le montant au lecteur ; renvoie le paiement créé, ou null (l'erreur est alors affichée). */
    async envoyer(factureId, montantEuros) {
        try {
            this.ecouter();
            this.paiement = await this.api.terminal.envoyer(factureId, montantEuros);
            this.suivre();

            return this.paiement;
        } catch (e) {
            this.rootStore.notificationStore.setNotification(messageErreur(e), "error");

            return null;
        }
    }

    suivre() {
        this.arreterSuivi();
        this.minuteurPaiement = setInterval(() => this.actualiser(), INTERVALLE_PAIEMENT_MS);
    }

    arreterSuivi() {
        clearInterval(this.minuteurPaiement);
        this.minuteurPaiement = null;
    }

    async actualiser() {
        if (!this.paiement) {
            return;
        }
        try {
            this.recevoir(await this.api.terminal.statut(this.paiement.id));
        } catch (e) {
            // réseau coupé un instant : on réessaie au prochain tour
        }
    }

    /** Annule la demande tant que le téléphone ne l'a pas prise. */
    async annuler() {
        if (!this.paiement) {
            return;
        }
        try {
            this.recevoir(await this.api.terminal.annuler(this.paiement.id));
        } catch (e) {
            this.rootStore.notificationStore.setNotification(messageErreur(e), "error");
        }
    }

    /** Ferme la fenêtre d'attente. */
    oublier() {
        this.arreterSuivi();
        this.paiement = null;
    }
}

decorate(TerminalStore, {
    etat: observable,
    paiement: observable,
});
